/**
 * Interactive product comparison tool.
 * Pure helpers are exported for Vitest.
 */

import { addLinesToCart, resolveCartErrorMessage } from '@theme/cart-add'
import { excludingTaxCents } from '@theme/price-excluding-tax'
import '@theme/quick-add'
import {
  addMediaQueryListener,
  isMobileBreakpoint,
  mediaQueryLarge,
  removeMediaQueryListener
} from '@theme/utilities'

/** @typedef {'available' | 'unavailable' | 'text'} CellMode */
/** @typedef {'compare' | 'all'} ComparisonView */

/**
 * @typedef {object} FeatureCell
 * @property {string} [label]
 * @property {string} [value]
 * @property {string} [text]
 */

/**
 * @typedef {object} ComparisonProduct
 * @property {string} id
 * @property {string} [handle]
 * @property {string} title
 * @property {string} [url]
 * @property {string} [imageUrl]
 * @property {string} [imageAlt]
 * @property {string} [priceLabel]
 * @property {number} [priceCents]
 * @property {string} [htLabel]
 * @property {boolean} [taxable]
 * @property {'product' | 'collection' | 'none'} [ctaKind]
 * @property {string | number} [variantId]
 * @property {number} [variantCount]
 * @property {boolean} [available]
 * @property {boolean} [directAdd]
 * @property {string} [searchTerm]
 * @property {Record<string, FeatureCell>} [features]
 */

/**
 * @typedef {object} CategoryData
 * @property {string} label
 * @property {ComparisonProduct[]} products
 * @property {{ id: string, label: string, group?: string }[]} features
 */

/**
 * @typedef {object} ComparisonCatalog
 * @property {Record<string, CategoryData>} categories
 * @property {{ category?: string, maxDesktop?: number, maxMobile?: number }} [defaults]
 */

/**
 * @typedef {object} ComparisonUrlState
 * @property {string} category
 * @property {ComparisonView} view
 * @property {string[]} ids
 */

const FEATURE_GROUP_RULES = [
  {
    label: 'Général',
    phrases: [
      'interface et notice',
      'interface / ecran',
      'ecran tactile',
      'ecran hd',
      'connexion au vehicule',
      'diagnostic sans-fil',
      'mises a jour',
      'garantie',
      'adaptateurs obd',
      'couverture marques',
      'plateforme / memoire',
      'protocoles',
      'positionnement'
    ]
  },
  {
    label: 'Diagnostic',
    phrases: [
      'diagnostic tous systemes',
      'diagnostic 4 systemes',
      'diagnostic obd',
      'diagnostic des poids-lourds',
      'systemes diagnostiques',
      'lecture et effacement',
      'suppression voyant moteur',
      'donnees en temps reel',
      'parametres en temps reel',
      'live data',
      'test des actuateurs',
      'test des actionneurs',
      'actionneurs',
      'test de batterie',
      'test batterie',
      'obd2',
      'autovin',
      'auto-identify',
      'topologie des modules'
    ]
  },
  {
    label: 'Fonctions spéciales',
    phrases: [
      'remise a zero',
      'fonctions de maintenance',
      'regeneration du fap',
      'filtre a particules',
      'codage des injecteurs',
      'programmation d’injecteurs',
      "programmation d'injecteurs",
      'injecteurs',
      'gestion du tpms',
      'gestion des pneumatiques',
      'remplacement plaquettes',
      'frein de stationnement',
      'angle de direction',
      'remplacement de batterie',
      'purge abs',
      'suspension pneumatique',
      'boite de vitesses',
      'climatisation',
      'pompe a carburant',
      'airbag',
      'reglages phares',
      'vanne egr',
      'toits ouvrants',
      'start/stop',
      'cerine',
      'ad-blue',
      'adblue',
      'capteur nox',
      'dates des entretiens',
      'changement de langue',
      'antidemarrage',
      'codages des cles',
      'reprogrammation des ecu',
      'j2534'
    ]
  }
]

/**
 * @param {string} value
 * @returns {string}
 */
export const normalizeForSearch = (value) => {
  if (typeof value !== 'string') return ''
  return value
    .normalize('NFD')
    .replace(/\p{M}/gu, '')
    .toLowerCase()
    .trim()
    .replace(/\s+/g, ' ')
}

/**
 * Infer a comparison feature group from its label when metaobject `groupe` is blank.
 *
 * @param {string} label
 * @returns {'Général' | 'Diagnostic' | 'Fonctions spéciales' | 'Autres' | ''}
 */
export const inferComparisonFeatureGroup = (label) => {
  const normalized = normalizeForSearch(label)
  if (!normalized) return ''

  for (const group of FEATURE_GROUP_RULES) {
    if (group.phrases.some((phrase) => normalized.includes(phrase))) {
      return /** @type {'Général' | 'Diagnostic' | 'Fonctions spéciales'} */ (
        group.label
      )
    }
  }

  return 'Autres'
}

/**
 * Coerce metaobject field shapes (string, 1-item array, `{ value|label }`) to a trim string.
 * @param {unknown} raw
 * @returns {string}
 */
export const normalizeCellText = (raw) => {
  if (raw == null) return ''
  if (typeof raw === 'string') return raw.trim()
  if (typeof raw === 'number' || typeof raw === 'boolean') {
    return String(raw).trim()
  }
  if (Array.isArray(raw)) {
    if (raw.length === 1) return normalizeCellText(raw[0])
    return ''
  }
  if (typeof raw === 'object') {
    const record = /** @type {Record<string, unknown>} */ (raw)
    if ('value' in record) return normalizeCellText(record.value)
    if ('label' in record) return normalizeCellText(record.label)
  }
  return ''
}

/**
 * @param {unknown} [raw]
 * @returns {CellMode}
 */
export const normalizeCellValue = (raw) => {
  const value = normalizeCellText(raw).toLowerCase()

  if (
    value === 'vrai' ||
    value === 'oui' ||
    value === 'yes' ||
    value === 'true'
  ) {
    return 'available'
  }

  if (value === 'uniquement texte' || value === 'texte' || value === 'text') {
    return 'text'
  }

  return 'unavailable'
}

/**
 * @param {string | null | undefined} value
 * @returns {ComparisonView}
 */
export const normalizeView = (value) =>
  String(value ?? '')
    .trim()
    .toLowerCase() === 'all'
    ? 'all'
    : 'compare'

/**
 * @param {boolean} isMobile
 * @param {{ maxDesktop?: number, maxMobile?: number }} [defaults]
 * @returns {number}
 */
export const resolveMaxSlots = (isMobile, defaults = {}) => {
  const desktop = Number(defaults.maxDesktop) || 3
  const mobile = Number(defaults.maxMobile) || 2
  return isMobile ? mobile : desktop
}

/**
 * @param {{ isMobile: boolean, view: ComparisonView, selectedCount: number, max: number, pickerExpanded: boolean }} state
 * @returns {{ comparisonReady: boolean, showToggle: boolean, pickerOpen: boolean }}
 */
export const resolvePickerDisclosureState = (state) => {
  const comparisonReady = state.view === 'compare' && state.selectedCount >= 2
  const showToggle = comparisonReady
  return {
    comparisonReady,
    showToggle,
    pickerOpen:
      state.view === 'compare' && (!comparisonReady || state.pickerExpanded)
  }
}

/**
 * @param {string} template
 * @param {Record<string, string | number>} vars
 * @returns {string}
 */
export const formatMessage = (template, vars) => {
  if (typeof template !== 'string') return ''
  return template.replace(/\{(\w+)\}/g, (_, key) =>
    vars[key] != null ? String(vars[key]) : ''
  )
}

/**
 * @param {string | null | undefined} query
 * @returns {{ category: string, ids: string[] } | null}
 */
export const parseCompareQuery = (query) => {
  if (!query || typeof query !== 'string') return null
  const trimmed = query.trim()
  if (!trimmed) return null

  const colonIndex = trimmed.indexOf(':')
  if (colonIndex <= 0) {
    // Bare category key without ids (legacy-friendly).
    if (/^[a-z0-9_-]+$/i.test(trimmed)) {
      return { category: trimmed, ids: [] }
    }
    return null
  }

  const category = trimmed.slice(0, colonIndex).trim()
  const idsPart = trimmed.slice(colonIndex + 1).trim()
  if (!category) return null

  const ids = idsPart
    ? idsPart
        .split(',')
        .map((id) => id.trim())
        .filter(Boolean)
    : []

  return { category, ids }
}

/**
 * @param {string} category
 * @param {string[]} ids
 * @returns {string}
 */
export const serializeCompareQuery = (category, ids) => {
  const cleanIds = (ids || []).filter(Boolean)
  if (!category || !cleanIds.length) return ''
  return `${category}:${cleanIds.join(',')}`
}

/**
 * @param {URLSearchParams | string | null | undefined} input
 * @returns {ComparisonUrlState}
 */
export const parseComparisonUrlState = (input) => {
  /** @type {URLSearchParams} */
  let params
  if (input instanceof URLSearchParams) {
    params = input
  } else if (typeof input === 'string') {
    const raw = input.startsWith('?') ? input.slice(1) : input
    params = new URLSearchParams(raw)
  } else {
    params = new URLSearchParams()
  }

  // Prefer `mode` — Shopify reserves `?view=` for alternate templates
  // (`page.*.all.json`), which would unload this page's sections.
  const view = normalizeView(params.get('mode') || params.get('view'))
  const parsed = parseCompareQuery(params.get('compare'))
  const categoryParam = String(params.get('category') || '').trim()

  if (parsed?.category) {
    return {
      category: parsed.category,
      view,
      ids: parsed.ids || []
    }
  }

  if (categoryParam) {
    return {
      category: categoryParam,
      view,
      ids: []
    }
  }

  return { category: '', view, ids: [] }
}

/**
 * @param {URLSearchParams} params
 * @param {ComparisonUrlState} state
 * @returns {URLSearchParams}
 */
export const applyComparisonUrlState = (params, state) => {
  const next = new URLSearchParams(params.toString())
  const category = String(state?.category || '').trim()
  const view = normalizeView(state?.view)
  const ids = (state?.ids || []).filter(Boolean)

  next.delete('compare')
  next.delete('category')
  next.delete('mode')
  // Strip legacy `view` so Shopify never treats it as an alternate template.
  next.delete('view')

  if (!category) return next

  if (ids.length) {
    next.set('compare', serializeCompareQuery(category, ids))
  } else {
    next.set('category', category)
  }

  if (view === 'all') {
    next.set('mode', 'all')
  }

  return next
}

/**
 * @param {ComparisonProduct[]} products
 * @param {string} featureId
 * @returns {string}
 */
export const cellSignature = (products, featureId) => {
  return products
    .map((product) => {
      const cell = product.features?.[featureId]
      const mode = normalizeCellValue(cell?.value)
      const text = normalizeCellText(cell?.text)
      return `${mode}:${text}`
    })
    .join('|')
}

/**
 * @param {{ id: string, label: string }[]} features
 * @param {ComparisonProduct[]} selectedProducts
 * @param {boolean} differencesOnly
 * @returns {{ id: string, label: string, differs: boolean }[]}
 */
export const buildVisibleRows = (
  features,
  selectedProducts,
  differencesOnly
) => {
  if (!Array.isArray(features) || !features.length) return []
  if (!Array.isArray(selectedProducts) || selectedProducts.length < 2) {
    return features.map((feature) => ({
      ...feature,
      differs: false
    }))
  }

  return features
    .map((feature) => {
      const signature = cellSignature(selectedProducts, feature.id)
      const parts = signature.split('|')
      const differs = parts.some((part) => part !== parts[0])
      return { ...feature, differs }
    })
    .filter((row) => (differencesOnly ? row.differs : true))
}

/**
 * Group visible rows by their optional feature group, preserving first-seen order.
 * A blank group stays untitled when any row has an explicit `group`.
 * When no row has an explicit `group` and rows match known automotive groups,
 * groups are inferred automatically from the feature labels.
 *
 * @param {{ id: string, label: string, group?: string, differs?: boolean }[]} rows
 * @returns {{ label: string, rows: { id: string, label: string, group?: string, differs?: boolean }[] }[]}
 */
export const groupVisibleRows = (rows) => {
  if (!Array.isArray(rows) || !rows.length) return []

  const hasExplicitGroup = rows.some((row) =>
    Boolean(normalizeCellText(row?.group))
  )
  const shouldInferGroups =
    !hasExplicitGroup &&
    rows.length >= 2 &&
    rows.some((row) => {
      const inferred = inferComparisonFeatureGroup(row?.label || '')
      return Boolean(inferred && inferred !== 'Autres')
    })

  /** @type {{ label: string, rows: typeof rows }[]} */
  const groups = []
  /** @type {Map<string, number>} */
  const indexByLabel = new Map()

  for (const row of rows) {
    const label = shouldInferGroups
      ? inferComparisonFeatureGroup(row?.label || '')
      : normalizeCellText(row?.group)
    const existing = indexByLabel.get(label)
    if (existing == null) {
      indexByLabel.set(label, groups.length)
      groups.push({ label, rows: [row] })
    } else {
      groups[existing].rows.push(row)
    }
  }

  return groups
}

/**
 * A direct cart add is only safe for one available variant with no required
 * bundle, paid option, or selling plan. Liquid sets `directAdd` after those checks.
 *
 * @param {ComparisonProduct | null | undefined} product
 * @returns {boolean}
 */
export const canDirectAdd = (product) => {
  if (!product || product.ctaKind !== 'product') return false
  if (product.available === false || product.directAdd !== true) return false
  if (product.variantId == null || product.variantId === '') return false
  return Number(product.variantCount) === 1
}

/**
 * @param {{ id: string, label: string }[]} features
 * @param {string} query
 * @returns {{ id: string, label: string }[]}
 */
export const filterFeaturesBySearch = (features, query) => {
  if (!Array.isArray(features) || !features.length) return []
  const normalized = normalizeForSearch(query)
  if (!normalized) return features
  return features.filter((feature) =>
    normalizeForSearch(feature.label || '').includes(normalized)
  )
}

/**
 * @param {ComparisonCatalog | null | undefined} catalog
 * @returns {string[]}
 */
export const availableCategoryKeys = (catalog) => {
  if (!catalog?.categories) return []
  return Object.keys(catalog.categories).filter((key) => {
    const category = catalog.categories[key]
    return Array.isArray(category?.products) && category.products.length > 0
  })
}

/**
 * @param {ComparisonCatalog} catalog
 * @param {string} preferred
 * @returns {string}
 */
export const resolveDefaultCategory = (catalog, preferred) => {
  const keys = availableCategoryKeys(catalog)
  if (!keys.length) return ''
  if (preferred && keys.includes(preferred)) return preferred
  const fallback = catalog.defaults?.category
  if (fallback && keys.includes(fallback)) return fallback
  return keys[0]
}

/** Known URL aliases → catalog category keys. */
const CATEGORY_ALIASES = {
  automobile: 'auto',
  'poids-lourd': 'poids_lourd'
}

/**
 * @param {string} category
 * @returns {string}
 */
export const normalizeCategoryKey = (category) => {
  const key = String(category || '').trim()
  if (!key) return ''
  return CATEGORY_ALIASES[key] || key
}

/**
 * @typedef {object} ComparisonRestoreState
 * @property {string} category
 * @property {ComparisonView} view
 * @property {string[]} ids
 * @property {boolean} openedFromUrl
 */

/**
 * Resolve shareable URL state for restore-on-reload.
 * Returns null when the URL has no comparison intent, or no category can be opened.
 *
 * @param {ComparisonCatalog | null | undefined} catalog
 * @param {URLSearchParams | string | null | undefined} search
 * @returns {ComparisonRestoreState | null}
 */
export const resolveComparisonRestoreState = (catalog, search) => {
  if (!catalog?.categories) return null

  /** @type {URLSearchParams} */
  let params
  if (search instanceof URLSearchParams) {
    params = search
  } else if (typeof search === 'string') {
    const raw = search.startsWith('?') ? search.slice(1) : search
    params = new URLSearchParams(raw)
  } else {
    params = new URLSearchParams()
  }

  const hasCategoryParam = Boolean(String(params.get('category') || '').trim())
  const hasCompareParam = Boolean(String(params.get('compare') || '').trim())
  const view = normalizeView(params.get('mode') || params.get('view'))
  const hasModeAll = view === 'all'
  const hasIntent = hasCategoryParam || hasCompareParam || hasModeAll
  if (!hasIntent) return null

  const state = parseComparisonUrlState(params)
  const preferred = normalizeCategoryKey(state.category)
  const category = resolveDefaultCategory(catalog, preferred)
  if (!category) return null

  const products = catalog.categories[category]?.products || []
  const validIds = new Set(products.map((product) => product.id))
  const ids =
    preferred === category
      ? (state.ids || []).filter((id) => validIds.has(id))
      : []

  return {
    category,
    view: state.view,
    ids,
    openedFromUrl: true
  }
}

/**
 * @param {string} svgMarkup
 * @param {string} className
 * @returns {string}
 */
const wrapIcon = (svgMarkup, className) =>
  `<span class="${className}" aria-hidden="true">${svgMarkup}</span>`

const FALLBACK_CHECK_SVG =
  '<svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 20 20" fill="none" focusable="false"><path d="M4.5 10.5 8 14l7.5-8" stroke="currentColor" stroke-width="1.75" stroke-linecap="round" stroke-linejoin="round"/></svg>'

const FALLBACK_CROSS_SVG =
  '<svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 20 20" fill="none" focusable="false"><path d="M5.5 5.5 14.5 14.5M14.5 5.5 5.5 14.5" stroke="currentColor" stroke-width="1.75" stroke-linecap="round"/></svg>'

/** @type {Record<string, string>} */
const CATEGORY_ICON_KEYS = {
  auto: 'car',
  automobile: 'car',
  moto: 'bike',
  poids_lourd: 'truck',
  'poids-lourd': 'truck'
}

/**
 * @typedef {object} CellIconSvgs
 * @property {string} [check]
 * @property {string} [cross]
 */

/**
 * @param {string} baseLabel
 * @param {string} text
 * @returns {string}
 */
const hybridAccessibleName = (baseLabel, text) =>
  text ? `${baseLabel} — ${text}` : baseLabel

/**
 * @param {FeatureCell | undefined} cell
 * @param {{ included: string, notIncluded: string }} labels
 * @param {CellIconSvgs} [icons]
 * @returns {string}
 */
export const renderCellHtml = (cell, labels, icons = {}) => {
  const mode = normalizeCellValue(cell?.value)
  const text = normalizeCellText(cell?.text)
  const checkSvg = icons.check || FALLBACK_CHECK_SVG
  const crossSvg = icons.cross || FALLBACK_CROSS_SVG
  const withCaptionClass = text
    ? ' product-comparison-tool__value--with-caption'
    : ''

  if (mode === 'available') {
    const accessibleName = hybridAccessibleName(labels.included, text)
    if (text) {
      return `<span class="product-comparison-tool__value product-comparison-tool__value--available${withCaptionClass}" role="img" aria-label="${escapeAttr(accessibleName)}">
      <span class="product-comparison-tool__icon product-comparison-tool__icon--check" aria-hidden="true">
        ${wrapIcon(checkSvg, 'product-comparison-tool__icon-inner')}
      </span>
      <span class="product-comparison-tool__caption" aria-hidden="true">${escapeHtml(text)}</span>
    </span>`
    }
    return `<span class="product-comparison-tool__value product-comparison-tool__value--available">
      <span class="product-comparison-tool__icon product-comparison-tool__icon--check" role="img" aria-label="${escapeAttr(labels.included)}">
        ${wrapIcon(checkSvg, 'product-comparison-tool__icon-inner')}
      </span>
    </span>`
  }

  if (mode === 'text' && text) {
    return `<span class="product-comparison-tool__value product-comparison-tool__value--text${withCaptionClass}">
      <span class="product-comparison-tool__text">${escapeHtml(text)}</span>
    </span>`
  }

  const accessibleName = hybridAccessibleName(labels.notIncluded, text)
  if (text) {
    return `<span class="product-comparison-tool__value product-comparison-tool__value--unavailable${withCaptionClass}" role="img" aria-label="${escapeAttr(accessibleName)}">
    <span class="product-comparison-tool__icon product-comparison-tool__icon--cross" aria-hidden="true">
      ${wrapIcon(crossSvg, 'product-comparison-tool__icon-inner')}
    </span>
    <span class="product-comparison-tool__caption" aria-hidden="true">${escapeHtml(text)}</span>
  </span>`
  }

  return `<span class="product-comparison-tool__value product-comparison-tool__value--unavailable">
    <span class="product-comparison-tool__icon product-comparison-tool__icon--cross" role="img" aria-label="${escapeAttr(labels.notIncluded)}">
      ${wrapIcon(crossSvg, 'product-comparison-tool__icon-inner')}
    </span>
  </span>`
}

/**
 * @param {string} value
 * @returns {string}
 */
export const escapeHtml = (value) =>
  String(value)
    .replace(/&/g, '&amp;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;')
    .replace(/"/g, '&quot;')

/**
 * TTC price plus optional HT sibling for comparison slots and picker cards.
 *
 * @param {ComparisonProduct} product
 * @param {{
 *   showHt?: boolean,
 *   excludingTaxLabel?: string,
 *   htSuffix?: string,
 *   formatAmount?: (cents: number) => string
 * }} [options]
 * @returns {string}
 */
export const buildComparisonPriceHtml = (product, options = {}) => {
  if (!product?.priceLabel) return ''

  const ttc = `<span class="product-comparison-tool__price-ttc">${escapeHtml(product.priceLabel)}</span>`
  const showHt = Boolean(options.showHt) && product.taxable !== false
  if (!showHt) return ttc

  const htCents = excludingTaxCents(product.priceCents)
  const htAmount =
    product.htLabel ||
    (htCents > 0 && typeof options.formatAmount === 'function'
      ? options.formatAmount(htCents)
      : '')

  if (!htAmount) return ttc

  return `<span class="price-ht-group">${ttc}<span class="price-ht"><span class="visually-hidden">${escapeHtml(options.excludingTaxLabel || '')}&nbsp;</span><span class="price-ht__amount">${escapeHtml(htAmount)}</span><span class="price-ht__suffix" aria-hidden="true">${escapeHtml(options.htSuffix || '')}</span></span></span>`
}

/**
 * @param {string} value
 * @returns {string}
 */
export const escapeAttr = (value) => escapeHtml(value).replace(/'/g, '&#39;')

/**
 * Lean product column heading for the comparison matrix (`thead`).
 * Image + title only by default (linked when URL exists); optional remove and price.
 *
 * @param {ComparisonProduct} product
 * @param {{ removeProduct?: string }} [msgs]
 * @param {{ canRemove?: boolean, removeIcon?: string, priceHtml?: string, includeImage?: boolean, unavailableLabel?: string }} [options]
 * @returns {string}
 */
export const buildProductHeadingHtml = (product, msgs = {}, options = {}) => {
  const title = product.title || ''
  const includeImage = options.includeImage !== false
  const image = includeImage
    ? product.imageUrl
      ? `<img class="product-comparison-tool__head-image" src="${escapeAttr(product.imageUrl)}" alt="${escapeAttr(product.imageAlt || '')}" width="80" height="80" loading="lazy" decoding="async">`
      : `<span class="product-comparison-tool__head-image product-comparison-tool__head-image--placeholder" aria-hidden="true"></span>`
    : ''
  const titleHtml = `<span class="product-comparison-tool__head-title">${escapeHtml(title)}</span>`
  const identity = product.url
    ? `<a class="product-comparison-tool__head-link" href="${escapeAttr(product.url)}">${image}${titleHtml}</a>`
    : `<div class="product-comparison-tool__head-identity">${image}${titleHtml}</div>`

  let remove = ''
  if (options.canRemove) {
    const removeLabel = formatMessage(msgs.removeProduct || '', { title })
    const icon = options.removeIcon || '<span aria-hidden="true">×</span>'
    remove = `<button
          type="button"
          class="product-comparison-tool__head-remove"
          data-comparison-head-remove
          data-product-id="${escapeAttr(product.id)}"
          aria-label="${escapeAttr(removeLabel)}"
        >${icon}</button>`
  }

  const price = options.priceHtml
    ? `<p class="product-comparison-tool__head-price">${options.priceHtml}</p>`
    : ''
  const unavailable =
    !product.url && !product.priceLabel && options.unavailableLabel
      ? `<p class="product-comparison-tool__unavailable">${escapeHtml(options.unavailableLabel)}</p>`
      : ''

  return `<th scope="col" class="product-comparison-tool__product-heading">
            <div class="product-comparison-tool__head-card">
              ${identity}
              ${price}
              ${unavailable}
              ${remove}
            </div>
          </th>`
}

/**
 * Figma v2 compared product card (grey rounded visual box, floating top-right
 * remove button, centered title and price underneath).
 *
 * @param {ComparisonProduct} product
 * @param {{ removeProduct?: string }} [msgs]
 * @param {{ canRemove?: boolean, removeIcon?: string, priceHtml?: string, unavailableLabel?: string }} [options]
 * @returns {string}
 */
export const buildComparedProductCardHtml = (
  product,
  msgs = {},
  options = {}
) => {
  const title = product.title || ''
  const image = product.imageUrl
    ? `<img class="product-comparison-tool__photo-img" src="${escapeAttr(product.imageUrl)}" alt="${escapeAttr(product.imageAlt || '')}" width="370" height="309" loading="lazy" decoding="async">`
    : `<span class="product-comparison-tool__photo-img product-comparison-tool__photo-img--placeholder" aria-hidden="true"></span>`
  const mediaInner = product.url
    ? `<a class="product-comparison-tool__photo-media-link" href="${escapeAttr(product.url)}" tabindex="-1" aria-hidden="true">${image}</a>`
    : image

  let remove = ''
  if (options.canRemove !== false) {
    const removeLabel = formatMessage(msgs.removeProduct || '', { title })
    const icon = options.removeIcon || '<span aria-hidden="true">×</span>'
    remove = `<button
        type="button"
        class="product-comparison-tool__head-remove"
        data-comparison-head-remove
        data-product-id="${escapeAttr(product.id)}"
        aria-label="${escapeAttr(removeLabel)}"
      >${icon}</button>`
  }

  const titleNode = product.url
    ? `<a class="product-comparison-tool__photo-title" href="${escapeAttr(product.url)}">${escapeHtml(title)}</a>`
    : `<span class="product-comparison-tool__photo-title">${escapeHtml(title)}</span>`

  const priceNode = options.priceHtml
    ? `<p class="product-comparison-tool__photo-price">${options.priceHtml}</p>`
    : !product.url && !product.priceLabel && options.unavailableLabel
      ? `<p class="product-comparison-tool__unavailable">${escapeHtml(options.unavailableLabel)}</p>`
      : ''

  return `<div class="product-comparison-tool__photo">
    <div class="product-comparison-tool__photo-media">
      ${mediaInner}
      ${remove}
    </div>
    <div class="product-comparison-tool__photo-meta">
      ${titleNode}
      ${priceNode}
    </div>
  </div>`
}

/**
 * Cart and product-page actions under one compared product.
 *
 * @param {ComparisonProduct} product
 * @param {Record<string, string>} [msgs]
 * @param {{ errorId?: string }} [options]
 * @returns {string}
 */
export const buildComparisonActionHtml = (product, msgs = {}, options = {}) => {
  const viewLabel =
    product.ctaKind === 'collection'
      ? msgs.viewCollection || ''
      : msgs.viewProduct || ''
  const viewLink = product.url
    ? `<a class="product-comparison-tool__view-link" href="${escapeAttr(product.url)}">${escapeHtml(viewLabel)}</a>`
    : ''

  if (product.ctaKind !== 'product') {
    const unavailable = !product.url
      ? `<p class="product-comparison-tool__unavailable">${escapeHtml(msgs.unavailablePurchase || '')}</p>`
      : ''
    return `<div class="product-comparison-tool__action">${unavailable}${viewLink}</div>`
  }

  const namedLabel = formatMessage(
    msgs.addToCartNamed || msgs.addToCart || '',
    {
      title: product.title || ''
    }
  )
  const errorId = options.errorId || ''
  const soldOut = product.available === false
  const label = soldOut ? msgs.soldOut || '' : msgs.addToCart || ''

  return `<div class="product-comparison-tool__action">
    <button
      type="button"
      class="product-comparison-tool__add"
      data-comparison-add
      data-product-id="${escapeAttr(product.id)}"
      aria-label="${escapeAttr(namedLabel)}"
      ${errorId ? `aria-describedby="${escapeAttr(errorId)}"` : ''}
      ${soldOut ? 'disabled' : ''}
    >${escapeHtml(label)}</button>
    <p
      class="product-comparison-tool__add-error"
      data-comparison-add-error
      role="alert"
      ${errorId ? `id="${escapeAttr(errorId)}"` : ''}
      hidden
    ></p>
    ${viewLink}
  </div>`
}

class ProductComparisonTool extends HTMLElement {
  /** @type {AbortController | null} */
  #abortController = null

  /** @type {((event: MediaQueryListEvent) => void) | null} */
  #viewportListener = null

  /** @type {ComparisonCatalog | null} */
  #catalog = null

  /** @type {Record<string, string>} */
  #msgs = {}

  /** @type {boolean} */
  #showHtPrice = false

  /** @type {string} */
  #category = ''

  /** @type {ComparisonView} */
  #view = 'compare'

  /** @type {string[]} */
  #selectedIds = []

  /** @type {boolean} */
  #differencesOnly = false

  /** @type {string} */
  #searchQuery = ''

  /** @type {number | null} */
  #activeSlotIndex = null

  /** @type {boolean} */
  #openedFromUrl = false

  /** @type {boolean} */
  #pickerExpanded = true

  /** @type {number | null} */
  #searchFrame = null

  /** @type {number | null} */
  #stickyFrame = null

  /** @type {string} */
  #categoryRenderKey = ''

  /** @type {Record<string, string>} */
  #icons = {}

  /** @type {boolean} */
  #adding = false

  connectedCallback() {
    if (this.#abortController) {
      this.#abortController.abort()
      this.#abortController = null
    }
    if (this.#viewportListener) {
      removeMediaQueryListener(mediaQueryLarge, this.#viewportListener)
      this.#viewportListener = null
    }

    this.#abortController = new AbortController()
    this.#catalog = this.#readJson('[data-comparison-tool-data]')
    this.#msgs = this.#readJson('[data-comparison-tool-msgs]') || {}
    this.#showHtPrice = this.dataset.showHtPrice === 'true'
    this.#icons = this.#readIcons()
    this.#categoryRenderKey = ''

    if (!this.#catalog?.categories) {
      this.hidden = true
      return
    }

    const keys = availableCategoryKeys(this.#catalog)
    if (!keys.length) {
      this.#announce(this.#msgs.noCategory || '')
      this.hidden = true
      return
    }

    const renderKey = `${keys.join('|')}::${this.#category}`
    if (renderKey === this.#categoryRenderKey) return
    this.#categoryRenderKey = renderKey

    this.hidden = false
    this.#restoreFromUrl()
    this.#trimSelectionToMax()
    this.#bindEvents()
    this.#render()

    if (this.#openedFromUrl && this.#category) {
      this.#scrollWorkspaceIntoView()
    }
  }

  disconnectedCallback() {
    this.#abortController?.abort()
    this.#abortController = null
    if (this.#searchFrame != null) {
      cancelAnimationFrame(this.#searchFrame)
      this.#searchFrame = null
    }
    if (this.#stickyFrame != null) {
      cancelAnimationFrame(this.#stickyFrame)
      this.#stickyFrame = null
    }
    if (this.#viewportListener) {
      removeMediaQueryListener(mediaQueryLarge, this.#viewportListener)
      this.#viewportListener = null
    }
  }

  /**
   * @param {string} selector
   * @returns {any}
   */
  #readJson(selector) {
    const node = this.querySelector(selector)
    if (!(node instanceof HTMLScriptElement)) return null
    try {
      return JSON.parse(node.textContent || 'null')
    } catch {
      return null
    }
  }

  /**
   * @returns {Record<string, string>}
   */
  #readIcons() {
    /** @type {Record<string, string>} */
    const icons = {}
    const template = this.querySelector('template[data-comparison-icons]')
    if (!(template instanceof HTMLTemplateElement)) return icons

    template.content
      .querySelectorAll('[data-comparison-icon]')
      .forEach((node) => {
        if (!(node instanceof HTMLElement)) return
        const key = node.dataset.comparisonIcon
        if (!key) return
        icons[key] = node.innerHTML.trim()
      })

    return icons
  }

  /**
   * @param {string} key
   * @returns {string}
   */
  #icon(key) {
    return this.#icons[key] || ''
  }

  /**
   * @param {string} key
   * @param {string} [className]
   * @returns {string}
   */
  #iconSpan(key, className = 'product-comparison-tool__btn-icon') {
    const svg = this.#icon(key)
    if (!svg) return ''
    return wrapIcon(svg, className)
  }

  #bindEvents() {
    const { signal } = this.#abortController
    const search = this.querySelector('[data-comparison-search]')
    const clear = this.querySelector('[data-comparison-clear]')
    const diffToggle = this.querySelector('[data-comparison-diff-toggle]')
    const viewToggle = this.querySelector('[data-comparison-toolbar]')
    const categories = this.querySelector('[data-comparison-categories]')
    const slots = this.querySelector('[data-comparison-slots]')
    const selectionControl = this.querySelector(
      '[data-comparison-selection-control]'
    )
    const pickerList = this.querySelector('[data-comparison-picker-list]')
    const pickerToggle = this.querySelector('[data-comparison-picker-toggle]')
    const matrixWrap = this.querySelector('[data-comparison-matrix-wrap]')
    const changeCategory = this.querySelector(
      '[data-comparison-change-category]'
    )
    const actions = this.querySelector('[data-comparison-actions]')

    search?.addEventListener(
      'input',
      (event) => {
        const target = event.target
        if (!(target instanceof HTMLInputElement)) return
        this.#searchQuery = target.value
        if (this.#searchFrame != null) cancelAnimationFrame(this.#searchFrame)
        this.#searchFrame = requestAnimationFrame(() => {
          this.#searchFrame = null
          if (this.#view === 'all') {
            this.#renderMatrix()
          } else {
            this.#renderPickerList()
          }
        })
      },
      { signal }
    )

    clear?.addEventListener(
      'click',
      () => {
        this.#selectedIds = []
        this.#activeSlotIndex = null
        this.#pickerExpanded = true
        this.#announce(this.#msgs.statusCleared || '')
        this.#syncUrl()
        this.#render()
      },
      { signal }
    )

    pickerToggle?.addEventListener(
      'click',
      (event) => {
        this.#pickerExpanded = !this.#pickerExpanded
        this.#renderSelectionControl()
        this.#renderPicker()
        if (this.#pickerExpanded && event.detail === 0) {
          this.#focusPickerCaption()
        }
      },
      { signal }
    )

    selectionControl?.addEventListener(
      'click',
      (event) => {
        const add =
          event.target instanceof Element
            ? event.target.closest('[data-comparison-slot-add]')
            : null
        if (add instanceof HTMLButtonElement) {
          this.#focusEmptySlot(add)
        }
      },
      { signal }
    )

    diffToggle?.addEventListener(
      'click',
      () => {
        this.#differencesOnly = !this.#differencesOnly
        if (diffToggle instanceof HTMLButtonElement) {
          const pressed = this.#differencesOnly ? 'true' : 'false'
          diffToggle.setAttribute('aria-pressed', pressed)
          diffToggle.setAttribute('aria-checked', pressed)
        }
        this.#renderMatrix()
      },
      { signal }
    )

    changeCategory?.addEventListener(
      'click',
      () => {
        this.#clearCategory()
      },
      { signal }
    )

    viewToggle?.addEventListener(
      'click',
      (event) => {
        const button =
          event.target instanceof Element
            ? event.target.closest('[data-comparison-view]')
            : null
        if (!(button instanceof HTMLButtonElement)) return
        const next = normalizeView(button.dataset.comparisonView)
        if (next === this.#view) return
        this.#setView(next)
      },
      { signal }
    )

    categories?.addEventListener(
      'click',
      (event) => {
        const button =
          event.target instanceof Element
            ? event.target.closest('[data-comparison-category]')
            : null
        if (!(button instanceof HTMLButtonElement)) return
        const next = button.dataset.comparisonCategory
        if (!next) return
        if (next === this.#category) {
          this.#clearCategory()
          return
        }
        this.#setCategory(next)
      },
      { signal }
    )

    slots?.addEventListener(
      'click',
      (event) => {
        const target = event.target instanceof Element ? event.target : null
        if (!target) return

        const remove = target.closest('[data-comparison-slot-remove]')
        if (remove instanceof HTMLButtonElement) {
          const id = remove.dataset.productId
          if (id) this.#removeProduct(id)
          return
        }

        const add = target.closest('[data-comparison-slot-add]')
        if (add instanceof HTMLButtonElement) {
          this.#focusEmptySlot(add)
        }
      },
      { signal }
    )

    pickerList?.addEventListener(
      'click',
      (event) => {
        const option =
          event.target instanceof Element
            ? event.target.closest('[data-comparison-product-id]')
            : null
        if (!(option instanceof HTMLButtonElement)) return
        const id = option.dataset.comparisonProductId
        if (!id) return
        if (this.#selectedIds.includes(id)) {
          this.#removeProduct(id)
        } else {
          this.#addProduct(id)
        }
      },
      { signal }
    )

    matrixWrap?.addEventListener(
      'click',
      (event) => {
        const remove =
          event.target instanceof Element
            ? event.target.closest('[data-comparison-head-remove]')
            : null
        if (remove instanceof HTMLButtonElement) {
          const id = remove.dataset.productId
          if (id) this.#removeProduct(id)
          return
        }

        const add =
          event.target instanceof Element
            ? event.target.closest('[data-comparison-slot-add]')
            : null
        if (add instanceof HTMLButtonElement) {
          this.#focusEmptySlot(add)
        }
      },
      { signal }
    )

    actions?.addEventListener(
      'click',
      (event) => {
        const button =
          event.target instanceof Element
            ? event.target.closest('[data-comparison-add]')
            : null
        if (!(button instanceof HTMLButtonElement) || button.disabled) return
        const id = button.dataset.productId
        if (!id) return
        event.preventDefault()
        this.#addProductToCart(button, id)
      },
      { signal }
    )

    window.addEventListener(
      'pageshow',
      (event) => {
        if (!event.persisted) return
        this.#restoreFromUrl()
        this.#trimSelectionToMax()
        this.#render()
        if (this.#openedFromUrl && this.#category) {
          this.#scrollWorkspaceIntoView()
        }
      },
      { signal }
    )

    document.addEventListener(
      'shopify:section:load',
      (event) => {
        const sectionId =
          event instanceof CustomEvent
            ? event.detail?.sectionId || event.detail?.section_id
            : null
        if (!sectionId) return
        const section = this.closest(`#shopify-section-${sectionId}`)
        if (!section || !section.contains(this)) return
        this.#restoreFromUrl()
        this.#trimSelectionToMax()
        this.#render()
      },
      { signal }
    )

    this.#viewportListener = () => {
      this.#trimSelectionToMax()
      if (this.#selectedIds.length < 2) this.#pickerExpanded = true
      this.#render()
    }
    addMediaQueryListener(mediaQueryLarge, this.#viewportListener)
    this.#watchHeaderClearance()
  }

  /**
   * Keep sticky names under the visible header, including when the announcement bar hides.
   */
  #watchHeaderClearance() {
    const { signal } = this.#abortController
    const sync = () => this.#scheduleStickyTop()

    sync()
    window.addEventListener('scroll', sync, { passive: true, signal })
    window.addEventListener('resize', sync, { signal })
    document.getElementById('header-group')?.addEventListener(
      'transitionend',
      (event) => {
        if (!(event instanceof TransitionEvent)) return
        if (event.propertyName !== 'top') return
        sync()
      },
      { signal }
    )
  }

  #scheduleStickyTop() {
    if (this.#stickyFrame != null) return
    this.#stickyFrame = requestAnimationFrame(() => {
      this.#stickyFrame = null
      const header = document.getElementById('header-group')
      const top = header
        ? Math.max(0, Math.round(header.getBoundingClientRect().bottom))
        : 0
      const next = `${top}px`
      if (this.style.getPropertyValue('--pct-sticky-top') !== next) {
        this.style.setProperty('--pct-sticky-top', next)
      }

      const photos = this.querySelector('[data-comparison-photos]')
      const cardsScrolled =
        this.#view === 'compare' &&
        photos instanceof HTMLElement &&
        !photos.hidden &&
        photos.getBoundingClientRect().bottom <= top + 8
      this.classList.toggle(
        'product-comparison-tool--cards-scrolled',
        cardsScrolled
      )
    })
  }

  /**
   * @param {string} category
   */
  #setCategory(category) {
    this.#category = category
    this.#selectedIds = []
    this.#activeSlotIndex = null
    this.#pickerExpanded = true
    this.#searchQuery = ''
    this.#resetSearchInput()
    const label = this.#catalog?.categories?.[category]?.label || category
    this.#announce(
      formatMessage(this.#msgs.statusCategory || '', { category: label })
    )
    this.#syncUrl()
    this.#render()
  }

  #clearCategory() {
    this.#category = ''
    this.#selectedIds = []
    this.#activeSlotIndex = null
    this.#pickerExpanded = true
    this.#searchQuery = ''
    this.#view = 'compare'
    this.#resetSearchInput()
    this.#announce(this.#msgs.statusCategoryClosed || '')
    this.#syncUrl()
    this.#render()
  }

  /**
   * @param {ComparisonView} view
   */
  #setView(view) {
    this.#view = view
    this.#pickerExpanded =
      view === 'compare' ? !this.#isComparisonReady() : false
    this.#searchQuery = ''
    this.#resetSearchInput()
    this.#announce(
      view === 'all'
        ? this.#msgs.statusViewAll || ''
        : this.#msgs.statusViewCompare || ''
    )
    this.#syncUrl()
    this.#render()
  }

  #resetSearchInput() {
    const search = this.querySelector('[data-comparison-search]')
    if (search instanceof HTMLInputElement) search.value = ''
  }

  /** Move the single search unit between picker (compare) and toolbar (all). */
  #syncSearchHost() {
    const unit = this.querySelector('[data-comparison-search-unit]')
    const pickerHost = this.querySelector(
      '[data-comparison-search-host="picker"]'
    )
    const toolbarHost = this.querySelector(
      '[data-comparison-search-host="toolbar"]'
    )
    if (
      !(unit instanceof HTMLElement) ||
      !(pickerHost instanceof HTMLElement) ||
      !(toolbarHost instanceof HTMLElement)
    ) {
      return
    }

    const useToolbar = this.#view === 'all' && Boolean(this.#category)
    const target = useToolbar ? toolbarHost : pickerHost
    if (unit.parentElement !== target) {
      target.appendChild(unit)
    }
    pickerHost.hidden = useToolbar
    toolbarHost.hidden = !useToolbar
  }

  /**
   * @param {string} id
   */
  #addProduct(id) {
    const max = resolveMaxSlots(
      isMobileBreakpoint(),
      this.#catalog?.defaults || {}
    )
    if (this.#selectedIds.includes(id)) return

    if (this.#selectedIds.length >= max) {
      if (
        this.#activeSlotIndex != null &&
        this.#activeSlotIndex < this.#selectedIds.length
      ) {
        this.#selectedIds[this.#activeSlotIndex] = id
      } else {
        this.#selectedIds[this.#selectedIds.length - 1] = id
      }
    } else if (
      this.#activeSlotIndex != null &&
      this.#activeSlotIndex < this.#selectedIds.length
    ) {
      this.#selectedIds.splice(this.#activeSlotIndex, 0, id)
      this.#selectedIds = this.#selectedIds.slice(0, max)
    } else {
      this.#selectedIds.push(id)
    }

    this.#activeSlotIndex = null
    if (this.#selectedIds.length >= 2) {
      this.#pickerExpanded = false
    }
    const product = this.#productById(id)
    if (product) {
      const selectedMsg = formatMessage(this.#msgs.statusSelected || '', {
        title: product.title
      })
      const fullMsg =
        this.#selectedIds.length >= max
          ? this.#msgs.statusSelectionFull || ''
          : ''
      this.#announce(fullMsg ? `${selectedMsg} ${fullMsg}`.trim() : selectedMsg)
    }
    this.#syncUrl()
    this.#render()
  }

  /**
   * @param {string} id
   */
  #removeProduct(id) {
    this.#selectedIds = this.#selectedIds.filter(
      (selectedId) => selectedId !== id
    )
    if (this.#selectedIds.length < 2) this.#pickerExpanded = true
    this.#syncUrl()
    this.#render()
  }

  #trimSelectionToMax() {
    if (this.#view === 'all') return
    const max = resolveMaxSlots(
      isMobileBreakpoint(),
      this.#catalog?.defaults || {}
    )
    if (this.#selectedIds.length > max) {
      this.#selectedIds = this.#selectedIds.slice(0, max)
      this.#syncUrl()
    }
  }

  #restoreFromUrl() {
    try {
      const restored = resolveComparisonRestoreState(
        this.#catalog,
        window.location.search
      )
      if (!restored) {
        this.#openedFromUrl = false
        return
      }
      this.#category = restored.category
      this.#view = restored.view
      this.#selectedIds = restored.ids
      this.#pickerExpanded = !(
        restored.view === 'compare' && restored.ids.length >= 2
      )
      this.#openedFromUrl = restored.openedFromUrl
      // Canonicalize shareable URL (`mode=all`, strip Shopify-reserved `view`).
      this.#syncUrl()
    } catch {
      // Ignore malformed URLs.
    }
  }

  #syncUrl() {
    try {
      const url = new URL(window.location.href)
      const nextParams = applyComparisonUrlState(url.searchParams, {
        category: this.#category,
        view: this.#view,
        ids: this.#selectedIds
      })
      url.search = nextParams.toString()
      window.history.replaceState({}, '', url)
    } catch {
      // Ignore history errors.
    }
  }

  #scrollWorkspaceIntoView() {
    const workspace = this.querySelector('[data-comparison-workspace]')
    if (!(workspace instanceof HTMLElement)) return
    const reduceMotion = window.matchMedia?.(
      '(prefers-reduced-motion: reduce)'
    )?.matches
    workspace.scrollIntoView({
      behavior: reduceMotion ? 'auto' : 'smooth',
      block: 'nearest'
    })
  }

  /**
   * @param {string} id
   * @returns {ComparisonProduct | undefined}
   */
  #productById(id) {
    return this.#catalog?.categories?.[this.#category]?.products?.find(
      (product) => product.id === id
    )
  }

  /**
   * @param {ComparisonProduct} product
   * @returns {string}
   */
  #priceHtml(product) {
    return buildComparisonPriceHtml(product, {
      showHt: this.#showHtPrice,
      excludingTaxLabel: this.#msgs.priceExcludingTax || '',
      htSuffix: this.#msgs.priceHtSuffix || ''
    })
  }

  /** @returns {ComparisonProduct[]} */
  #selectedProducts() {
    return this.#selectedIds.map((id) => this.#productById(id)).filter(Boolean)
  }

  /** @returns {ComparisonProduct[]} */
  #matrixProducts() {
    if (this.#view === 'all') {
      return this.#catalog?.categories?.[this.#category]?.products || []
    }
    return this.#selectedProducts()
  }

  /**
   * @param {string} message
   */
  #announce(message) {
    const status = this.querySelector('[data-comparison-status]')
    if (!(status instanceof HTMLElement) || !message) return
    status.textContent = ''
    requestAnimationFrame(() => {
      status.textContent = message
    })
  }

  #focusPickerCaption() {
    const caption = this.querySelector('[data-comparison-picker-caption]')
    if (caption instanceof HTMLElement) {
      caption.focus({ preventScroll: true })
    }
  }

  #focusPickerSearch() {
    const search = this.querySelector('[data-comparison-search]')
    if (search instanceof HTMLInputElement) {
      search.focus({ preventScroll: true })
    }
  }

  /**
   * @param {HTMLButtonElement} button
   */
  #focusEmptySlot(button) {
    const index = Number(button.dataset.slotIndex)
    this.#activeSlotIndex = Number.isFinite(index) ? index : null
    this.#pickerExpanded = true
    this.#renderSlots()
    this.#renderSelectionControl()
    this.#renderPicker()
    const picker = this.querySelector('[data-comparison-picker]')
    if (picker instanceof HTMLElement && !picker.hidden) {
      const reduceMotion = window.matchMedia?.(
        '(prefers-reduced-motion: reduce)'
      )?.matches
      picker.scrollIntoView({
        behavior: reduceMotion ? 'auto' : 'smooth',
        block: 'nearest'
      })
    }
    if (!isMobileBreakpoint()) this.#focusPickerSearch()
  }

  /**
   * @param {HTMLButtonElement} button
   * @param {string} id
   */
  async #addProductToCart(button, id) {
    if (this.#adding) return
    const product = this.#productById(id)
    if (!product || product.available === false) return

    const error = button.parentElement?.querySelector(
      '[data-comparison-add-error]'
    )
    if (error instanceof HTMLElement) {
      error.hidden = true
      error.textContent = ''
    }

    if (!canDirectAdd(product)) {
      await this.#openQuickAdd(product)
      return
    }

    this.#adding = true
    const previousLabel = button.textContent
    button.disabled = true
    button.setAttribute('aria-busy', 'true')
    button.textContent = this.#msgs.adding || previousLabel

    try {
      const result = await addLinesToCart({
        items: [{ id: product.variantId, quantity: 1 }],
        source: 'product-comparison-tool',
        sourceId: String(product.id),
        context: 'comparison',
        productId: String(product.id),
        initiator: button
      })

      if (!result.ok && error instanceof HTMLElement) {
        error.textContent =
          resolveCartErrorMessage(result.data) || this.#msgs.addError || ''
        error.hidden = false
        if (error.id) button.setAttribute('aria-describedby', error.id)
      }
    } finally {
      this.#adding = false
      button.disabled = false
      button.removeAttribute('aria-busy')
      button.textContent = previousLabel
    }
  }

  /**
   * @param {ComparisonProduct} product
   */
  async #openQuickAdd(product) {
    if (!product.url) return
    const host = document.createElement('quick-add-component')
    host.dataset.productUrl = product.url
    host.dataset.variantId =
      product.variantId == null ? '' : String(product.variantId)
    host.dataset.productTitle = product.title || ''
    host.dataset.productImageUrl = product.imageUrl || ''
    host.dataset.productPriceLabel = product.priceLabel || ''
    host.hidden = true
    this.appendChild(host)

    try {
      if (typeof host.handleClick === 'function') {
        await host.handleClick(new MouseEvent('click', { bubbles: true }))
      }
    } finally {
      host.remove()
    }
  }

  #isComparisonReady() {
    return this.#view === 'compare' && this.#selectedIds.length >= 2
  }

  #workspaceId() {
    return (
      this.dataset.comparisonWorkspaceId ||
      this.querySelector('[data-comparison-workspace]')?.id ||
      ''
    )
  }

  #render() {
    this.#renderCategories()
    this.#renderWorkspaceChrome()
    this.#syncSearchHost()
    this.#renderSlots()
    this.#renderSelectionControl()
    this.#renderPicker()
    this.#renderMatrix()
    this.#scheduleStickyTop()
  }

  #renderCategories() {
    const host = this.querySelector('[data-comparison-categories]')
    const change = this.querySelector('[data-comparison-change-category]')
    const activeBadge = this.querySelector('[data-comparison-active-category]')
    if (!(host instanceof HTMLElement) || !this.#catalog) return

    const keys = availableCategoryKeys(this.#catalog)
    if (!keys.length || this.#category) {
      host.hidden = true
      host.replaceChildren()
      if (change instanceof HTMLButtonElement) {
        change.hidden = !this.#category
      }
      if (activeBadge instanceof HTMLElement) {
        if (this.#category) {
          const activeCategory = this.#catalog.categories?.[this.#category]
          const activeLabel = activeCategory?.label || this.#category
          const iconKey = CATEGORY_ICON_KEYS[this.#category] || 'car'
          const iconHtml = this.#iconSpan(
            iconKey,
            'product-comparison-tool__active-category-icon'
          )
          activeBadge.innerHTML = `${iconHtml}<span>${escapeHtml(activeLabel)}</span>`
          activeBadge.hidden = false
        } else {
          activeBadge.replaceChildren()
          activeBadge.hidden = true
        }
      }
      return
    }

    if (change instanceof HTMLButtonElement) change.hidden = true
    if (activeBadge instanceof HTMLElement) {
      activeBadge.replaceChildren()
      activeBadge.hidden = true
    }

    const workspaceId = this.#workspaceId()
    host.hidden = false
    host.innerHTML = keys
      .map((key) => {
        const category = this.#catalog.categories[key]
        const label = category?.label || key
        const count = category?.products?.length || 0
        const countLabel = formatMessage(this.#msgs.productCount || '', {
          count
        })
        const iconKey = CATEGORY_ICON_KEYS[key] || 'car'
        const categoryIcon = this.#iconSpan(
          iconKey,
          'product-comparison-tool__category-icon-svg'
        )
        return `<button
          type="button"
          class="product-comparison-tool__category"
          data-comparison-category="${escapeAttr(key)}"
          aria-pressed="false"
          ${workspaceId ? `aria-controls="${escapeAttr(workspaceId)}"` : ''}
        >
          <span class="product-comparison-tool__category-icon" aria-hidden="true">${categoryIcon}</span>
          <span class="product-comparison-tool__category-copy">
            <span class="product-comparison-tool__category-title">${escapeHtml(label)}</span>
            <span class="product-comparison-tool__category-count">${escapeHtml(countLabel)}</span>
          </span>
        </button>`
      })
      .join('')
  }

  #renderWorkspaceChrome() {
    const workspace = this.querySelector('[data-comparison-workspace]')
    const toolbar = this.querySelector('[data-comparison-toolbar]')
    const gateHint = this.querySelector('[data-comparison-gate-hint]')
    const compare = this.querySelector('[data-comparison-compare]')
    const clear = this.querySelector('[data-comparison-clear]')
    const search = this.querySelector('[data-comparison-search]')
    const searchLabel = this.querySelector('[data-comparison-search-label]')
    const diffToggle = this.querySelector('[data-comparison-diff-toggle]')
    const viewButtons = this.querySelectorAll('[data-comparison-view]')
    const caption = this.querySelector('[data-comparison-caption]')
    const change = this.querySelector('[data-comparison-change-category]')
    const open = Boolean(this.#category)

    if (workspace instanceof HTMLElement) {
      workspace.hidden = !open
      if (open && change instanceof HTMLElement && change.id) {
        workspace.setAttribute('aria-labelledby', change.id)
      } else {
        workspace.removeAttribute('aria-labelledby')
      }
    }

    if (toolbar instanceof HTMLElement) {
      toolbar.hidden = !open
    }

    if (gateHint instanceof HTMLElement) {
      gateHint.hidden = open
    }

    if (compare instanceof HTMLElement) {
      compare.hidden = !open || this.#view !== 'compare'
    }

    if (clear instanceof HTMLButtonElement) {
      clear.hidden = this.#view !== 'compare' || this.#selectedIds.length === 0
    }

    viewButtons.forEach((button) => {
      if (!(button instanceof HTMLButtonElement)) return
      const pressed =
        normalizeView(button.dataset.comparisonView) === this.#view
      button.setAttribute('aria-pressed', pressed ? 'true' : 'false')
    })

    if (diffToggle instanceof HTMLButtonElement) {
      const pressed = this.#differencesOnly ? 'true' : 'false'
      diffToggle.setAttribute('aria-pressed', pressed)
      diffToggle.setAttribute('aria-checked', pressed)
    }

    if (search instanceof HTMLInputElement) {
      const placeholder =
        this.#view === 'all'
          ? this.#msgs.searchFeaturesPlaceholder ||
            this.#msgs.searchPlaceholder ||
            ''
          : this.#msgs.searchPlaceholder || ''
      search.placeholder = placeholder
    }

    if (searchLabel instanceof HTMLElement) {
      searchLabel.textContent =
        this.#view === 'all'
          ? this.#msgs.searchFeaturesLabel || this.#msgs.searchLabel || ''
          : this.#msgs.searchLabel || ''
    }

    if (caption instanceof HTMLElement) {
      caption.textContent =
        this.#view === 'all'
          ? this.#msgs.matrixCaptionAll || this.#msgs.matrixCaption || ''
          : this.#msgs.matrixCaption || ''
    }

    this.classList.toggle(
      'product-comparison-tool--view-all',
      this.#view === 'all'
    )
    this.classList.toggle('product-comparison-tool--open', open)
  }

  #renderSlots() {
    const host = this.querySelector('[data-comparison-slots]')
    if (!(host instanceof HTMLElement)) return
    if (!this.#category || this.#view !== 'compare') {
      host.hidden = true
      host.replaceChildren()
      return
    }

    const max = resolveMaxSlots(
      isMobileBreakpoint(),
      this.#catalog?.defaults || {}
    )
    const products = this.#selectedProducts()
    if (products.length >= 2) {
      host.hidden = true
      host.replaceChildren()
      return
    }

    host.hidden = false
    const plusIcon = this.#iconSpan(
      'plus',
      'product-comparison-tool__slot-add-icon-svg'
    )
    const closeIcon = this.#iconSpan(
      'close',
      'product-comparison-tool__slot-remove-icon'
    )

    host.innerHTML = Array.from({ length: max }, (_, index) => {
      const product = products[index]
      const slotLabel = formatMessage(this.#msgs.slotLabel || '', {
        index: index + 1
      })
      const isActive = this.#activeSlotIndex === index

      if (!product) {
        return `<div class="product-comparison-tool__slot product-comparison-tool__slot--empty${isActive ? ' product-comparison-tool__slot--active' : ''}">
          <button
            type="button"
            class="product-comparison-tool__slot-add"
            data-comparison-slot-add
            data-slot-index="${index}"
            aria-label="${escapeAttr(slotLabel)}"
            aria-pressed="${isActive ? 'true' : 'false'}"
          >
            <span class="product-comparison-tool__slot-add-icon" aria-hidden="true">${plusIcon}</span>
            <span>${escapeHtml(this.#msgs.addProduct || '')}</span>
          </button>
        </div>`
      }

      const removeLabel = formatMessage(this.#msgs.removeProduct || '', {
        title: product.title
      })
      const image = product.imageUrl
        ? `<img class="product-comparison-tool__slot-image" src="${escapeAttr(product.imageUrl)}" alt="${escapeAttr(product.imageAlt || '')}" width="48" height="48" loading="lazy" decoding="async">`
        : `<span class="product-comparison-tool__slot-image product-comparison-tool__slot-image--placeholder" aria-hidden="true"></span>`

      return `<div class="product-comparison-tool__slot product-comparison-tool__slot--filled">
        ${image}
        <div class="product-comparison-tool__slot-body">
          <p class="product-comparison-tool__slot-title">${escapeHtml(product.title)}</p>
          ${product.priceLabel ? `<p class="product-comparison-tool__slot-price">${this.#priceHtml(product)}</p>` : ''}
        </div>
        <button
          type="button"
          class="product-comparison-tool__slot-remove"
          data-comparison-slot-remove
          data-product-id="${escapeAttr(product.id)}"
          aria-label="${escapeAttr(removeLabel)}"
        >
          ${closeIcon || '<span aria-hidden="true">×</span>'}
        </button>
      </div>`
    }).join('')
  }

  #renderSelectionControl() {
    const control = this.querySelector('[data-comparison-selection-control]')
    const toggle = this.querySelector('[data-comparison-picker-toggle]')
    const label = this.querySelector('[data-comparison-picker-toggle-label]')
    const summary = this.querySelector('[data-comparison-selection-summary]')
    if (
      !(control instanceof HTMLElement) ||
      !(toggle instanceof HTMLButtonElement)
    ) {
      return
    }

    const max = resolveMaxSlots(
      isMobileBreakpoint(),
      this.#catalog?.defaults || {}
    )
    const state = resolvePickerDisclosureState({
      isMobile: isMobileBreakpoint(),
      view: this.#view,
      selectedCount: this.#selectedIds.length,
      max,
      pickerExpanded: this.#pickerExpanded
    })

    control.hidden = !this.#category || !state.showToggle
    toggle.setAttribute('aria-expanded', state.pickerOpen ? 'true' : 'false')
    if (label instanceof HTMLElement) {
      label.textContent = state.pickerOpen
        ? this.#msgs.hideProducts || ''
        : this.#msgs.modifyProducts || ''
    }
    if (summary instanceof HTMLElement) {
      summary.textContent = formatMessage(this.#msgs.selectionCount || '', {
        current: this.#selectedIds.length,
        max
      })
    }

    let addMoreBtn = control.querySelector('[data-comparison-slot-add]')
    const canAddMore =
      state.comparisonReady &&
      !state.pickerOpen &&
      this.#selectedIds.length < max
    if (canAddMore) {
      if (!(addMoreBtn instanceof HTMLButtonElement)) {
        addMoreBtn = document.createElement('button')
        addMoreBtn.type = 'button'
        addMoreBtn.className = 'product-comparison-tool__add-more-btn'
        addMoreBtn.setAttribute('data-comparison-slot-add', '')
        control.appendChild(addMoreBtn)
      }
      addMoreBtn.dataset.slotIndex = String(this.#selectedIds.length)
      const plusIcon = this.#iconSpan(
        'plus',
        'product-comparison-tool__btn-icon'
      )
      addMoreBtn.innerHTML = `${plusIcon}<span>${escapeHtml(this.#msgs.addProduct || '')}</span>`
      addMoreBtn.hidden = false
    } else if (addMoreBtn instanceof HTMLElement) {
      addMoreBtn.hidden = true
    }

    this.classList.toggle(
      'product-comparison-tool--comparison-ready',
      state.comparisonReady
    )
    this.classList.toggle(
      'product-comparison-tool--picker-expanded',
      state.pickerOpen
    )
  }

  #renderPicker() {
    const picker = this.querySelector('[data-comparison-picker]')
    if (!(picker instanceof HTMLElement)) return

    if (!this.#category || this.#view !== 'compare') {
      picker.hidden = true
      return
    }

    const category = this.#catalog?.categories?.[this.#category]
    const hasProducts =
      Array.isArray(category?.products) && category.products.length > 0
    const max = resolveMaxSlots(
      isMobileBreakpoint(),
      this.#catalog?.defaults || {}
    )
    const disclosure = resolvePickerDisclosureState({
      isMobile: isMobileBreakpoint(),
      view: this.#view,
      selectedCount: this.#selectedIds.length,
      max,
      pickerExpanded: this.#pickerExpanded
    })
    picker.hidden = !hasProducts || !disclosure.pickerOpen
    if (picker.hidden) return
    this.#renderPickerMeta()
    this.#renderPickerList()
  }

  #renderPickerMeta() {
    const countEl = this.querySelector('[data-comparison-picker-count]')
    const selectionEl = this.querySelector('[data-comparison-picker-selection]')
    const category = this.#catalog?.categories?.[this.#category]
    const products = category?.products || []
    const max = resolveMaxSlots(
      isMobileBreakpoint(),
      this.#catalog?.defaults || {}
    )
    const selected = this.#selectedIds.length
    const atMax = selected >= max

    if (countEl instanceof HTMLElement) {
      countEl.textContent = formatMessage(this.#msgs.productCount || '', {
        count: products.length
      })
    }

    if (selectionEl instanceof HTMLElement) {
      selectionEl.textContent = atMax
        ? formatMessage(this.#msgs.selectionFull || '', { max })
        : formatMessage(this.#msgs.selectionCount || '', {
            current: selected,
            max
          })
      selectionEl.classList.toggle('is-full', atMax)
    }
  }

  #renderPickerList() {
    const list = this.querySelector('[data-comparison-picker-list]')
    const empty = this.querySelector('[data-comparison-picker-empty]')
    if (!(list instanceof HTMLElement)) return

    const category = this.#catalog?.categories?.[this.#category]
    const products = category?.products || []
    const query = normalizeForSearch(this.#searchQuery)
    const max = resolveMaxSlots(
      isMobileBreakpoint(),
      this.#catalog?.defaults || {}
    )
    const atMax = this.#selectedIds.length >= max

    const filtered = products.filter((product) => {
      if (!query) return true
      const haystack = normalizeForSearch(
        product.searchTerm || product.title || ''
      )
      return haystack.includes(query)
    })

    if (empty instanceof HTMLElement) {
      empty.hidden = filtered.length > 0
    }

    list.innerHTML = filtered
      .map((product) => {
        const selected = this.#selectedIds.includes(product.id)
        const disabled = atMax && !selected
        const media = product.imageUrl
          ? `<span class="product-comparison-tool__picker-media"><img src="${escapeAttr(product.imageUrl)}" alt="" width="80" height="80" loading="lazy" decoding="async"></span>`
          : `<span class="product-comparison-tool__picker-media product-comparison-tool__picker-media--placeholder" aria-hidden="true"></span>`
        const checkMark = this.#iconSpan(
          'checkmark',
          'product-comparison-tool__picker-check-icon'
        )
        return `<button
          type="button"
          class="product-comparison-tool__picker-item${selected ? ' is-selected' : ''}${disabled ? ' is-disabled' : ''}"
          aria-pressed="${selected ? 'true' : 'false'}"
          data-comparison-product-id="${escapeAttr(product.id)}"
          ${disabled ? 'disabled aria-disabled="true"' : ''}
        >
          <span class="product-comparison-tool__picker-check" aria-hidden="true">${checkMark}</span>
          ${media}
          <span class="product-comparison-tool__picker-copy">
            <span class="product-comparison-tool__picker-title">${escapeHtml(product.title)}</span>
            ${
              product.priceLabel
                ? `<span class="product-comparison-tool__picker-price">${this.#priceHtml(product)}</span>`
                : !product.url
                  ? `<span class="product-comparison-tool__unavailable">${escapeHtml(this.#msgs.unavailablePurchase || '')}</span>`
                  : ''
            }
          </span>
        </button>`
      })
      .join('')
  }

  #renderMatrix() {
    const wrap = this.querySelector('[data-comparison-matrix-wrap]')
    const hint = this.querySelector('[data-comparison-hint]')
    const thead = this.querySelector('[data-comparison-thead]')
    const tbody = this.querySelector('[data-comparison-tbody]')
    const featuresEmpty = this.querySelector('[data-comparison-features-empty]')
    const diffToggle = this.querySelector('[data-comparison-diff-toggle]')

    if (
      !(wrap instanceof HTMLElement) ||
      !(thead instanceof HTMLElement) ||
      !(tbody instanceof HTMLElement)
    ) {
      return
    }

    const actions = this.querySelector('[data-comparison-actions]')
    const photos = this.querySelector('[data-comparison-photos]')
    const clearPhotos = () => {
      if (!(photos instanceof HTMLElement)) return
      photos.hidden = true
      photos.replaceChildren()
    }

    if (!this.#category) {
      wrap.hidden = true
      thead.innerHTML = ''
      tbody.innerHTML = ''
      clearPhotos()
      if (actions instanceof HTMLElement) {
        actions.hidden = true
        actions.replaceChildren()
      }
      if (hint instanceof HTMLElement) hint.hidden = true
      if (featuresEmpty instanceof HTMLElement) featuresEmpty.hidden = true
      if (diffToggle instanceof HTMLButtonElement) diffToggle.hidden = true
      this.style.removeProperty('--pct-columns')
      this.style.removeProperty('--pct-head-columns')
      return
    }

    const products = this.#matrixProducts()
    const isAll = this.#view === 'all'
    const showMatrix = isAll ? products.length >= 1 : products.length >= 2

    if (hint instanceof HTMLElement) {
      hint.hidden = isAll || showMatrix || this.#selectedProducts().length === 0
    }

    wrap.hidden = !showMatrix
    if (diffToggle instanceof HTMLButtonElement) {
      diffToggle.hidden = !showMatrix || products.length < 2
    }
    if (!showMatrix) {
      thead.replaceChildren()
      tbody.replaceChildren()
      clearPhotos()
      if (actions instanceof HTMLElement) {
        actions.hidden = true
        actions.replaceChildren()
      }
      if (featuresEmpty instanceof HTMLElement) featuresEmpty.hidden = true
      this.style.removeProperty('--pct-columns')
      this.style.removeProperty('--pct-head-columns')
      return
    }

    if (diffToggle instanceof HTMLButtonElement) {
      const pressed = this.#differencesOnly ? 'true' : 'false'
      diffToggle.setAttribute('aria-pressed', pressed)
      diffToggle.setAttribute('aria-checked', pressed)
    }

    const features = this.#catalog?.categories?.[this.#category]?.features || []
    const searchable = isAll
      ? filterFeaturesBySearch(features, this.#searchQuery)
      : features
    const rows = buildVisibleRows(searchable, products, this.#differencesOnly)

    if (featuresEmpty instanceof HTMLElement) {
      featuresEmpty.hidden = rows.length > 0
    }

    const canRemove = !isAll
    this.style.setProperty('--pct-columns', String(products.length))
    this.style.setProperty('--pct-head-columns', String(products.length))

    const removeIcon = this.#iconSpan(
      'close',
      'product-comparison-tool__head-remove-icon'
    )

    if (photos instanceof HTMLElement) {
      photos.hidden = isAll
      photos.innerHTML = isAll
        ? ''
        : products
            .map((product) =>
              buildComparedProductCardHtml(product, this.#msgs, {
                canRemove,
                removeIcon,
                priceHtml: this.#priceHtml(product),
                unavailableLabel:
                  !product.url && !product.priceLabel
                    ? this.#msgs.unavailablePurchase || ''
                    : ''
              })
            )
            .join('')
    }

    thead.innerHTML = `<tr>
      <th scope="col" class="product-comparison-tool__feature-heading">${escapeHtml(this.#msgs.featureHeading || '')}</th>
      ${products
        .map((product) =>
          buildProductHeadingHtml(product, this.#msgs, {
            canRemove,
            removeIcon,
            includeImage: true,
            unavailableLabel:
              !product.url && !product.priceLabel
                ? this.#msgs.unavailablePurchase || ''
                : '',
            priceHtml: this.#priceHtml(product)
          })
        )
        .join('')}
    </tr>`

    const cellLabels = {
      included: this.#msgs.included || 'Included',
      notIncluded: this.#msgs.notIncluded || 'Not included'
    }
    const cellIcons = {
      check: this.#icon('checkmark') || FALLBACK_CHECK_SVG,
      cross: this.#icon('close') || FALLBACK_CROSS_SVG
    }
    const groups = groupVisibleRows(rows)
    tbody.innerHTML = groups
      .map((group) => {
        const heading = group.label
          ? `<tr class="product-comparison-tool__group-row">
              <th class="product-comparison-tool__group-title" scope="colgroup" colspan="${products.length + 1}">${escapeHtml(group.label)}</th>
            </tr>`
          : ''
        const body = group.rows
          .map((row) => {
            const rowClass = row.differs
              ? 'product-comparison-tool__row product-comparison-tool__row--diff'
              : 'product-comparison-tool__row product-comparison-tool__row--same'
            return `<tr class="${rowClass}">
          <th scope="row" class="product-comparison-tool__feature">${escapeHtml(row.label)}</th>
          ${products
            .map((product) => {
              const cell = product.features?.[row.id]
              return `<td class="product-comparison-tool__cell">${renderCellHtml(cell, cellLabels, cellIcons)}</td>`
            })
            .join('')}
        </tr>`
          })
          .join('')
        return `${heading}${body}`
      })
      .join('')

    if (actions instanceof HTMLElement) {
      const showActions = !isAll
      actions.hidden = !showActions
      actions.innerHTML = showActions
        ? products
            .map((product, index) =>
              buildComparisonActionHtml(product, this.#msgs, {
                errorId: `ProductComparisonTool-add-error-${index}`
              })
            )
            .join('')
        : ''
    }
  }
}

if (!customElements.get('product-comparison-tool')) {
  customElements.define('product-comparison-tool', ProductComparisonTool)
}
